import React from 'react';
import { Download, Eye, Mail, MoreVertical, Printer, Trash2 } from 'lucide-react';
import { AnchoredMenuPanel, useAnchoredMenu } from '../../../../components/AnchoredMenu';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { printPdfFromUrl } from '../../../../utils/printPdf';

const PANEL_WIDTH = 208;

/**
 * Drei-Punkte-Menü für ein Dokument im Verlauf (Öffnen, Drucken, Herunterladen, Per E-Mail senden, Löschen).
 * `overlay` positioniert den Trigger oben rechts über einer Vorschau-Kachel.
 * Ohne `onDelete` entfällt „Datei löschen“ (z. B. steuerrelevante Rechnungs-PDFs);
 * ohne `printUrl` / `onShareMail` entfallen Drucken / Mail (nur für PDFs übergeben).
 */
export function LeadDocumentActionsMenu({
  filename,
  onOpen,
  onDelete,
  printUrl,
  downloadUrl,
  onShareMail,
  variant = 'card',
}: {
  filename: string;
  onOpen: () => void;
  onDelete?: () => void;
  printUrl?: string;
  downloadUrl?: string;
  onShareMail?: () => void | Promise<void>;
  variant?: 'card' | 'overlay';
}) {
  const itemCount = 1 + (printUrl ? 1 : 0) + (downloadUrl ? 1 : 0) + (onShareMail ? 1 : 0) + (onDelete ? 1 : 0);
  const menu = useAnchoredMenu({ panelWidth: PANEL_WIDTH, panelHeight: itemCount * 36 + (onDelete ? 25 : 8) });
  const { open, setOpen, triggerRef } = menu;
  const [busy, setBusy] = React.useState<'print' | 'mail' | null>(null);

  const itemClass = 'flex w-full items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-xs font-medium';
  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };
  /** PDF laden dauert: Menü bleibt offen und zeigt den Spinner, bis Druckdialog bzw. Mail-Fenster bereit ist. */
  const runAsync = (key: 'print' | 'mail', action: () => void | Promise<void>) => async () => {
    if (busy) return;
    setBusy(key);
    try {
      await action();
    } finally {
      setBusy(null);
      setOpen(false);
    }
  };
  const busyIcon = <LoadingSpinner size="sm" />;

  return (
    <div className={variant === 'overlay' ? 'absolute top-1.5 right-1.5 z-20' : 'relative shrink-0'}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Aktionen für ${filename}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className={`rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 ${
          variant === 'overlay'
            ? 'p-1.5 bg-app-surface/80 text-app-text-subtle backdrop-blur-sm transition-colors hover:bg-app-surface hover:text-app-text'
            : 'p-1.5 text-app-text-subtle hover:bg-app-hover hover:text-app-text'
        }`}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      <AnchoredMenuPanel menu={menu} width={PANEL_WIDTH}>
        <button type="button" role="menuitem" className={`${itemClass} text-app-text-muted hover:bg-app-hover`} onClick={run(onOpen)}>
          <Eye className="h-4 w-4 text-app-text-subtle" />
          Öffnen
        </button>
        {printUrl ? (
          <button
            type="button"
            role="menuitem"
            className={`${itemClass} text-app-text-muted hover:bg-app-hover`}
            disabled={busy != null}
            // Klappt der Druck nicht (z. B. Popup/iframe blockiert), PDF im neuen Tab öffnen — dort lässt es sich drucken.
            onClick={runAsync('print', () =>
              printPdfFromUrl(printUrl).catch(() => {
                window.open(printUrl, '_blank', 'noopener');
              }),
            )}
          >
            {busy === 'print' ? busyIcon : <Printer className="h-4 w-4 text-app-text-subtle" />}
            Drucken
          </button>
        ) : null}
        {downloadUrl ? (
          <a
            role="menuitem"
            href={downloadUrl}
            download={filename || true}
            onClick={() => setOpen(false)}
            className={`${itemClass} text-app-text-muted hover:bg-app-hover`}
          >
            <Download className="h-4 w-4 text-app-text-subtle" />
            Herunterladen
          </a>
        ) : null}
        {onShareMail ? (
          <button
            type="button"
            role="menuitem"
            disabled={busy != null}
            className={`${itemClass} text-app-text-muted hover:bg-app-hover`}
            onClick={runAsync('mail', onShareMail)}
          >
            {busy === 'mail' ? busyIcon : <Mail className="h-4 w-4 text-app-text-subtle" />}
            Per E-Mail senden
          </button>
        ) : null}
        {onDelete ? (
          <>
            <div className="my-1 border-t border-app-border" role="separator" />
            <button type="button" role="menuitem" className={`${itemClass} text-red-600 hover:bg-red-50`} onClick={run(onDelete)}>
              <Trash2 className="h-4 w-4" />
              Datei löschen
            </button>
          </>
        ) : null}
      </AnchoredMenuPanel>
    </div>
  );
}
